import type { NxMention } from '../Mentions/types';
import { foldText } from './text';

/** One slash command offered by `NxChatComposer`. */
export interface NxChatCommand {
  /** The command word without the slash, e.g. `aktivität`. */
  name: string;
  /** Argument hint shown after the name, e.g. `@Person Text`. */
  hint?: string;
  /** What the command does, e.g. „Aktivität anlegen“. */
  description?: string;
  /** Further words that run the same command. Case and accents never matter. */
  aliases?: readonly string[];
}

/** A sent message whose first word is a known command. */
export interface NxChatCommandInvocation {
  /** The command's `name`, also when an alias was typed. */
  name: string;
  /** Everything after the command word, trimmed. */
  args: string;
  /** The whole message, trimmed. */
  text: string;
  /** The @-mentions inside `args`; `start` / `end` index into `args`. */
  mentions: NxMention[];
}

function words(command: NxChatCommand): string[] {
  return [command.name, ...(command.aliases ?? [])].map(foldText);
}

/** Commands whose name or an alias starts with `query` (the text after the slash). */
export function matchChatCommands(
  commands: readonly NxChatCommand[],
  query: string
): NxChatCommand[] {
  const q = foldText(query);
  return commands.filter((command) => words(command).some((word) => word.startsWith(q)));
}

/** True when `query` is exactly the command's name or one of its aliases. */
export function isExactChatCommand(command: NxChatCommand, query: string): boolean {
  const q = foldText(query);
  return words(command).includes(q);
}

/**
 * The slash-command query at the caret: the text between a leading „/“ and
 * the caret while the caret is still inside the first word; `null` otherwise.
 */
export function chatCommandQueryAt(text: string, caret: number): string | null {
  if (!text.startsWith('/') || caret < 1) return null;
  const query = text.slice(1, caret);
  return /\s/.test(query) ? null : query;
}

/**
 * Reads a message as a command: its first word must be „/“ plus the name or
 * an alias of one of `commands`, otherwise the result is `null`. `mentions`
 * index into `text` and come back re-based onto `args`.
 */
export function parseChatCommand(
  text: string,
  commands: readonly NxChatCommand[],
  mentions: readonly NxMention[] = []
): NxChatCommandInvocation | null {
  const lead = text.length - text.trimStart().length;
  const trimmed = text.trim();
  const match = /^\/(\S+)/.exec(trimmed);
  if (!match) return null;
  const command = commands.find((candidate) => isExactChatCommand(candidate, match[1]));
  if (!command) return null;
  const rest = trimmed.slice(match[0].length);
  const argsStart = lead + match[0].length + (rest.length - rest.trimStart().length);
  const args = rest.trim();
  return {
    name: command.name,
    args,
    text: trimmed,
    mentions: mentions
      .filter((mention) => mention.start >= argsStart && mention.end - argsStart <= args.length)
      .map((mention) => ({
        ...mention,
        start: mention.start - argsStart,
        end: mention.end - argsStart,
      })),
  };
}
